import { Injectable } from "@angular/core";
import { UIStore } from "./ui.store";
import { StatisticsMetric } from "source/app/components/statistics/statistics.types";
import { NumberGridConfiguration, UiState, WidgetKey } from "./ui.store";
import { UIDeviceSettings } from "./location-state.service";
import { LocationStateQuery } from "./location-state.query";

@Injectable({ providedIn: "root" })
export class UiService {
	constructor(
		private uiStore: UIStore,
		private locationStateQuery: LocationStateQuery,
	) {
		this.listenForLocationStateChanges();
	}

	setSnapshot = (snapshot: UiState) => {
		this.uiStore.store.update(() => snapshot);
	};

	reset = () => {
		this.uiStore.reset();
	};

	private listenForLocationStateChanges = () => {
		this.locationStateQuery.rideOpsSettings$.subscribe((rideOpsSettings) => {
			if (rideOpsSettings.lockDeviceSettings) {
				this.setLockedUI(rideOpsSettings.deviceSettings);
			} else {
				this.setUnlockedUI();
			}
			this.setDefaultCarts(rideOpsSettings.dispatchUnits);
			this.setDefaultNumberGridAmount(rideOpsSettings.dispatchUnitCapacity);
			this.clampNumberGridAmountToCapacity(rideOpsSettings.dispatchUnitCapacity);

			// Handle Safari AI widget visibility
			const safariAiEnabled = rideOpsSettings.externalDataSettings?.safariAiEnabled ?? false;
			if (safariAiEnabled == false) {
				const widgetOptions = this.uiStore.store.getValue().widgetOptions;
				if (widgetOptions.safariAiDispatch?.visible) {
					this.setWidgetVisibility("safariAiDispatch", false);
				}
				if (widgetOptions.safariAiQueue?.visible) {
					this.setWidgetVisibility("safariAiQueue", false);
				}
			}

			// Handle bulletin widget visibility
			const bulletinWidgetEnabled = rideOpsSettings.bulletinWidgetSettings?.bulletinWidgetEnabled ?? false;
			if (bulletinWidgetEnabled == false) {
				this.setWidgetVisibility("bulletinWidget", false);
			}
		});
	};

	setRidersVisible = (visible: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			widgetOptions: {
				...state.widgetOptions,
				riders: {
					...state.widgetOptions.riders,
					visible: visible,
				},
			},
		}));
	};

	// Per-device local setting for the asset-dispatch metric, letting the operator flip between
	// counting occupied riders and empty seats. Distinct from the legacy riders-widget device metric.
	setAssetDispatchMetric = (metric: "emptySeats" | "occupiedSeats") => {
		return this.uiStore.store.update((state) => ({
			...state,
			assetDispatchMetric: metric,
		}));
	};

	// Operator-facing local choice of asset-dispatch input widget (numpad vs counter), letting the
	// operator switch how they enter the dispatch count during the day. Independent of the legacy
	// riders-widget device-level input mode.
	setAssetDispatchInputMode = (mode: "grid" | "selector") => {
		return this.uiStore.store.update((state) => ({
			...state,
			assetDispatchInputMode: mode,
		}));
	};

	setDashboardStatisticsMetric = (metric: StatisticsMetric) => {
		return this.uiStore.store.update((state) => ({
			...state,
			dashboardStatisticsMetric: metric,
		}));
	};

	setQueueTimeVisible = (visible: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			widgetOptions: {
				...state.widgetOptions,
				queueTime: { visible: visible },
			},
		}));
	};

	setAllowChangingDispatchesWhenClosingLocation = (value: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			allowChangingDispatchesWhenClosingLocation: value,
		}));
	};

	setAllowChangingRidersWhenClosingLocation = (value: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			allowChangingRidersWhenClosingLocation: value,
		}));
	};

	setRidersMode = (mode: "grid" | "selector") => {
		return this.uiStore.store.update((state) => ({
			...state,
			widgetOptions: {
				...state.widgetOptions,
				riders: {
					...state.widgetOptions.riders,
					visible: true,
					mode: mode,
				},
			},
		}));
	};

	setRidersInputMetric = (metric: "emptySeats" | "occupiedSeats") => {
		return this.uiStore.store.update((state) => ({
			...state,
			widgetOptions: {
				...state.widgetOptions,
				riders: {
					...state.widgetOptions.riders,
					metric: metric,
				},
			},
		}));
	};

	setNumberGridConfiguration = (configuration: NumberGridConfiguration) => {
		const amount = this.clampNumberGridAmount(configuration.amount);
		return this.uiStore.store.update((state) => ({
			...state,
			widgetOptions: {
				...state.widgetOptions,
				riders: {
					...state.widgetOptions.riders,
					numberGridConfiguration: {
						...configuration,
						amount,
					},
				},
			},
		}));
	};

	updateSeatsUnavailable = async (seatsUnavailable: number): Promise<void> => {
		const rideOpsSettings = await this.locationStateQuery.rideOpsSettings$.firstAsync();

		this.uiStore.store.update((state) => {
			const totalCapacity = rideOpsSettings.dispatchUnitCapacity * state.cartsInUse;
			const next = this.calculateSeatsUnavailable(totalCapacity, seatsUnavailable);

			return { ...state, seatsUnavailable: next };
		});
	};

	setUnitAvailable = (unitId: string, available: boolean): void => {
		this.uiStore.store.update((state) => {
			const ids = new Set(state.unavailableUnitIds ?? []);
			if (available) {
				ids.delete(unitId);
			} else {
				ids.add(unitId);
			}
			return { ...state, unavailableUnitIds: [...ids] };
		});
	};

	toggleSeatUnavailable = (unitId: string, seatId: string): void => {
		this.uiStore.store.update((state) => {
			const byUnit = { ...(state.unavailableSeatIdsByUnitId ?? {}) };
			const seats = new Set(byUnit[unitId] ?? []);
			if (seats.has(seatId)) {
				seats.delete(seatId);
			} else {
				seats.add(seatId);
			}
			if (seats.size === 0) {
				delete byUnit[unitId];
			} else {
				byUnit[unitId] = [...seats];
			}
			return { ...state, unavailableSeatIdsByUnitId: byUnit };
		});
	};

	increaseCartsInUse = async () => {
		const rideOpsSettings = await this.locationStateQuery.rideOpsSettings$.firstAsync();
		const maxCarts = Math.max(1, rideOpsSettings.dispatchUnits || 1);

		this.uiStore.store.update((state) => {
			const cartsInUse = Math.min(state.cartsInUse + 1, maxCarts);
			const seatsUnavailable = this.calculateSeatsUnavailable(
				rideOpsSettings.dispatchUnitCapacity * cartsInUse,
				state.seatsUnavailable,
			);

			return {
				...state,
				defaultCartsHasBeenSet: true, // Should probably be called cartsInUseHasBeenManuallySet, but for backwards compatibility it is not renamed
				cartsInUse: cartsInUse,
				seatsUnavailable,
			};
		});
	};

	decreaseCartsInUse = async () => {
		const rideOpsSettings = await this.locationStateQuery.rideOpsSettings$.firstAsync();

		this.uiStore.store.update((state) => {
			const cartsInUse = Math.max(state.cartsInUse - 1, 1);
			const seatsUnavailable = this.calculateSeatsUnavailable(
				rideOpsSettings.dispatchUnitCapacity * cartsInUse,
				state.seatsUnavailable,
			);

			return {
				...state,
				defaultCartsHasBeenSet: true, // Should probably be called cartsInUseHasBeenManuallySet, but for backwards compatibility it is not renamed
				cartsInUse: cartsInUse,
				seatsUnavailable,
			};
		});
	};

	setDefaultCarts = async (carts: number) => {
		const rideOpsSettings = await this.locationStateQuery.rideOpsSettings$.firstAsync();
		const maxCarts = Math.max(1, rideOpsSettings.dispatchUnits || 1);

		this.uiStore.store.update((state) => {
			const cartsInUse = Math.min(maxCarts, Math.max(1, state.defaultCartsHasBeenSet ? state.cartsInUse : carts));
			const seatsUnavailable = this.calculateSeatsUnavailable(
				rideOpsSettings.dispatchUnitCapacity * cartsInUse,
				state.seatsUnavailable,
			);

			return {
				...state,
				cartsInUse: cartsInUse,
				seatsUnavailable,
			};
		});
	};

	private calculateSeatsUnavailable = (totalCapacity: number, seatsUnavailable: number): number => {
		// Value cant be higher than totalcapacity
		// value cant be lower than 0
		seatsUnavailable = Math.min(totalCapacity, seatsUnavailable);
		seatsUnavailable = Math.max(0, seatsUnavailable);

		return seatsUnavailable;
	};

	setLockedUI = (deviceSettings: UIDeviceSettings) => {
		this.uiStore.store.update((state) => ({
			...state,
			lockedSettings: true,

			allowChangingDispatchesWhenClosingLocation: deviceSettings.allowChangingDispatchesWhenClosingLocation,
			allowChangingRidersWhenClosingLocation: deviceSettings.allowChangingRidersWhenClosingLocation,

			widgetOptions: {
				...state.widgetOptions,
				queueTime: {
					...state.widgetOptions.queueTime,
					visible: deviceSettings.showQueueTimeWidget,
				},
				riders: {
					...state.widgetOptions.riders,
					visible: deviceSettings.showDispatchWidget,
					mode: deviceSettings.dispatchWidgetType,
					numberGridConfiguration: {
						...state.widgetOptions.riders.numberGridConfiguration,
						direction: deviceSettings.dispatchGridNumberDirection,
						amount: this.clampNumberGridAmount(deviceSettings.maximumRidersPerDispatch),
						amountConfigured: true,
					},
					metric: deviceSettings.dispatchMetric,
				},
				safariAiDispatch: {
					...state.widgetOptions.safariAiDispatch,
					visible: deviceSettings.showSafariAiDispatchWidget,
				},
				safariAiQueue: {
					...state.widgetOptions.safariAiQueue,
					visible: deviceSettings.showSafariAiQueueTimeWidget,
				},
				bulletinWidget: {
					...state.widgetOptions.bulletinWidget,
					visible: deviceSettings.showBulletinWidget,
				},
			},
		}));
	};

	setUnlockedUI = () => {
		this.uiStore.store.update((state) => ({ ...state, lockedSettings: false }));
	};

	setDefaultNumberGridAmount = (amount: number) => {
		const clamped = this.clampNumberGridAmount(amount);
		this.uiStore.store.update((state) => {
			if (state.widgetOptions.riders.numberGridConfiguration.amountConfigured) {
				return state;
			}
			return {
				...state,
				widgetOptions: {
					...state.widgetOptions,
					riders: {
						...state.widgetOptions.riders,
						numberGridConfiguration: {
							...state.widgetOptions.riders.numberGridConfiguration,
							amount: clamped,
						},
					},
				},
			};
		});
	};

	private clampNumberGridAmountToCapacity = (capacity: number) => {
		const max = Math.max(0, capacity || 0);
		this.uiStore.store.update((state) => {
			const current = state.widgetOptions.riders.numberGridConfiguration.amount;
			const amount = Math.min(max, Math.max(0, current));
			if (amount === current) {
				return state;
			}
			return {
				...state,
				widgetOptions: {
					...state.widgetOptions,
					riders: {
						...state.widgetOptions.riders,
						numberGridConfiguration: {
							...state.widgetOptions.riders.numberGridConfiguration,
							amount,
						},
					},
				},
			};
		});
	};

	private clampNumberGridAmount = (amount: number): number => {
		const max = Math.max(0, this.locationStateQuery.getSnapshot().rideOpsSettings.dispatchUnitCapacity || 0);
		return Math.min(max, Math.max(0, amount || 0));
	};

	setRideOpsUiV2OptIn = (enabled: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			rideOpsUiV2OptIn: enabled,
		}));
	};

	setRideOpsUiV2FlagAvailable = (available: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			rideOpsUiV2FlagAvailable: available,
		}));
	};

	setAssetHierarchyPrototypeFlagAvailable = (available: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			assetHierarchyPrototypeFlagAvailable: available,
		}));
	};

	setDispatchUndoOptIn = (enabled: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			dispatchUndoOptIn: enabled,
		}));
	};

	setRememberRiderCount = (enabled: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			rememberRiderCount: enabled,
		}));
	};

	setWidgetVisibility = (widgetKey: WidgetKey, visible: boolean) => {
		return this.uiStore.store.update((state) => ({
			...state,
			widgetOptions: {
				...state.widgetOptions,
				[widgetKey]: { visible: visible },
			},
		}));
	};
}
